'use client';

import { z } from 'zod';
import { HOMEPAGE_FEATURES, HOMEPAGE_LANDING_AUDIENCES, VISITOR_ACTIONS } from '@telegamez/types';
import { analyticsLocation, ensureGoogleAnalytics } from './google-analytics';

export const homepageAnalyticsEventSchema = z.enum([
  'view', 'start', 'connected', 'connection_error', 'reconnect', 'spotlight',
  'tour_start', 'chapter_view', 'tour_complete', 'question', 'control', 'video',
  'experience_view', 'experience_action', 'reaction', 'end', 'cta_click', 'login_click', 'menu_open', 'nav_click',
]);
export type HomepageAnalyticsEvent = z.infer<typeof homepageAnalyticsEventSchema>;

// An explicit schema strips unknown fields. Never accept names, text, URLs, tokens, or session IDs.
export const homepageAnalyticsParametersSchema = z.object({
  phase: z.enum(['entry', 'spotlight', 'tour', 'conversation', 'expired']).optional(),
  audience: z.enum(HOMEPAGE_LANDING_AUDIENCES).optional(),
  message_index: z.number().int().min(1).max(3).optional(),
  feature: z.enum(HOMEPAGE_FEATURES).optional(),
  chapter_index: z.number().int().min(1).max(10).optional(),
  experiences_used: z.number().int().min(0).max(10).optional(),
  success: z.boolean().optional(),
  chapters_seen: z.number().int().min(0).max(10).optional(),
  elapsed_seconds: z.number().int().min(0).max(300).optional(),
  source: z.enum(['automatic', 'manual', 'voice', 'header', 'entry', 'closing', 'site_menu', 'control_panel', 'video_player']).optional(),
  action: z.enum([...VISITOR_ACTIONS, 'open', 'close', 'select', 'show_feature', 'play', 'pause', 'complete', 'error', 'stop', 'mute', 'unmute', 'microphone_on', 'microphone_off', 'microphone_request', 'fullscreen', 'help', 'return_to_landing']).optional(),
  input_method: z.enum(['text', 'voice']).optional(),
  blocked: z.boolean().optional(),
  has_video: z.boolean().optional(),
  end_reason: z.enum(['expired', 'leave', 'navigation']).optional(),
  destination: z.enum(['home', 'about', 'legal', 'contact', 'register', 'dashboard']).optional(),
  item_index: z.number().int().min(0).max(4).optional(),
});
export type HomepageAnalyticsParameters = z.infer<typeof homepageAnalyticsParametersSchema>;

export function trackHomepageEvent(event: HomepageAnalyticsEvent, parameters: HomepageAnalyticsParameters = {}): void {
  try {
    if (!ensureGoogleAnalytics()) return;
    const name = homepageAnalyticsEventSchema.parse(event);
    const values = homepageAnalyticsParametersSchema.parse(parameters);
    window.gtag!('event', `homepage_room_${name}`, {
      experience: 'visitor_room', page_path: '/', page_location: analyticsLocation(window.location.href), ...values,
    });
  } catch { /* Analytics failures must never affect the visitor's room. */ }
}
